import React from 'react';

import { PrimaryButton, ProButton } from '@components/Button/variants';
import IconCheckMark from '@components/Images/Rebranding/IconCheckMark';
import { TiModalColorScheme } from '@components/TIModal';
import useEventTracking from '@hooks/useEventTracking';
import { root_path } from '@routes';
import { ClickTarget, click } from '@tracking';

import { MAGENTA, PARAGRAPH_STYLES, PRO, PRO_GOLD } from '../constants';
import PlanDetails from './PlanDetails';
import { CONFIRMATION_ANCILLARY_TEXT, CONFIRMATION_TEXT, ConfirmationTypes } from './constants';
import { SubscriptionConfirmationProps } from './interfaces';

const GO_TO_HOMEPAGE = 'Go to the Homepage';

const SubscriptionConfirmation = ({
  billingCycle,
  cancelReason,
  confirmationType,
  nextBillingAmountWithTax,
  nextBillingDate,
  subscriptionPlan,
  theme = TiModalColorScheme.light,
}: SubscriptionConfirmationProps): JSX.Element => {
  const confirmationText = CONFIRMATION_TEXT[confirmationType];
  const { headline, subheadline, closing } = confirmationText;
  const isProSubscriber = subscriptionPlan?.includes(PRO);
  const iconColor = isProSubscriber ? PRO_GOLD : MAGENTA;
  const textColor = theme === TiModalColorScheme.dark ? 'text-white' : 'text-black';
  const { trackEvent } = useEventTracking();
  const CTAButton = isProSubscriber ? ProButton : PrimaryButton;
  return (
    <>
      <header>
        <IconCheckMark
          className={`mx-auto block h-40 w-40 rounded-full border-2 border-solid p-6 ${iconColor}`}
        />
        <h1
          className={`m-0 mt-16 font-suisse-works text-headline-xs !font-medium sm:text-headline-sm ${textColor}`}
        >
          {headline}
        </h1>
        <p className={`${PARAGRAPH_STYLES} ${textColor}`}>{subheadline}</p>
      </header>

      <PlanDetails
        billingCycle={billingCycle}
        nextBillingAmountWithTax={nextBillingAmountWithTax}
        nextBillingDate={nextBillingDate}
        subscriptionPlan={subscriptionPlan}
        theme={theme}
        confirmationType={confirmationType}
      />

      {closing && (
        <p className={`${PARAGRAPH_STYLES} ${textColor} mt-32`}>
          {closing} {CONFIRMATION_ANCILLARY_TEXT?.[cancelReason]}
        </p>
      )}

      {confirmationType === ConfirmationTypes.PauseSubscription && (
        <div className="flex justify-center">
          <CTAButton
            as="link"
            size="small"
            className="mt-32"
            href={root_path()}
            onClickCapture={() =>
              trackEvent(
                click({
                  clickTarget: ClickTarget.GoToHomepage,
                  clickText: GO_TO_HOMEPAGE,
                })
              )
            }
          >
            {GO_TO_HOMEPAGE}
          </CTAButton>
        </div>
      )}
    </>
  );
};

export default SubscriptionConfirmation;
